import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OptionSelect, { IOption } from './OptionSelect';
import ChecklistIcon from '../../icons/IconLib/ChecklistIcon';

/** Order status filter: -2 — all statuses (0 is a real status — פתוחה). */
export const ORDER_STATUS = {
    ALL: -2,
    OPEN: 0,
    LIKUT: 1,
    CHECKED: 2,
    LIKUT_DONE: 3,
    READY_TO_SHIP: 4,
    READY_FOR_LIKUT: 5,
    COMPLETIONS: 7,
    CLOSED: 9,
} as const;

export type OrderStatusValue = typeof ORDER_STATUS[keyof typeof ORDER_STATUS];

interface Props {
    value: OrderStatusValue;
    onSelect: (value: OrderStatusValue) => void;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    leadingIcon?: React.ReactNode | null;
    className?: string;
}

// Status names stay in Hebrew — the same labels as the status badges on the order rows.
const STATUS_OPTIONS: IOption[] = [
    { value: ORDER_STATUS.OPEN, label: 'פתוחה' },
    { value: ORDER_STATUS.LIKUT, label: 'ליקוט' },
    { value: ORDER_STATUS.CHECKED, label: 'הזמנה נבדקה' },
    { value: ORDER_STATUS.LIKUT_DONE, label: 'סיום ליקוט' },
    { value: ORDER_STATUS.COMPLETIONS, label: 'השלמות' },
    { value: ORDER_STATUS.READY_TO_SHIP, label: 'מוכנה למשלוח' },
    { value: ORDER_STATUS.READY_FOR_LIKUT, label: 'מוכנה לליקוט' },
    { value: ORDER_STATUS.CLOSED, label: 'סגורה' },
];

const OrderStatusSelector: React.FC<Props> = ({
    value,
    onSelect,
    placeholder,
    disabled,
    readOnly,
    leadingIcon,
    className,
}) => {
    const [t] = useTranslation('selectors');

    const options = useMemo<IOption[]>(
        () => [{ value: ORDER_STATUS.ALL, label: t('selectors_orderStatus_all') }, ...STATUS_OPTIONS],
        [t],
    );

    return (
        <OptionSelect
            options={options}
            value={options.find((o) => o.value === value) ?? null}
            onSelect={(o) => onSelect((o?.value ?? ORDER_STATUS.ALL) as OrderStatusValue)}
            placeholder={placeholder ?? t('selectors_orderStatus_placeholder')}
            disabled={disabled}
            readOnly={readOnly}
            leadingIcon={leadingIcon === undefined ? <ChecklistIcon /> : leadingIcon}
            className={className}
        />
    );
};

export default OrderStatusSelector;
